import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TextInput, 
  toast,
  Select,
  DataTable
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizations } from '../../hooks/useOrganizations';
import { organizationService } from '../../services/organizationService';
import type { Organization, OrganizationType } from '../../types/organization';
import type { ActionDefinition } from '../../utils/tableHelpers';
import { ORGANIZATION_TYPE_LABELS } from '../../types/organization';
import { PermissionGate } from '../../components/PermissionGate';
import {
  EditIcon,
  TrashIcon,
  EyeIcon,
} from 'lucide-react';

// ✅ NEW: Import ALL column patterns for 100% pattern-based columns
import { 
  textColumn,
  actionsColumn,
  dateColumn,
  activeColumn,
  numberColumn
} from '../../utils/columns';

export function OrganizationManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  
  const {
    organizations,
    total,
    totalPages,
    currentPage,
    pageSize,
    loading,
    error,
    filters,
    updateFilters,
    refresh,
    fetchOrganizations,
  } = useOrganizations();

  // Handle search
  const handleSearch = useCallback((value: string) => {
    const newFilters = {
      ...filters,
      search: value || undefined,
    };
    updateFilters(newFilters);
    fetchOrganizations(1, newFilters);
  }, [filters, updateFilters, fetchOrganizations]);

  // Handle type filter
  const handleTypeFilter = useCallback((type: OrganizationType | 'all') => {
    const newFilters = {
      ...filters,
      type: type === 'all' ? undefined : type,
    };
    updateFilters(newFilters);
    fetchOrganizations(1, newFilters);
  }, [filters, updateFilters, fetchOrganizations]);

  // Handle status filter
  const handleStatusFilter = useCallback((status: 'all' | 'active' | 'inactive') => {
    const newFilters = {
      ...filters,
      isActive: status === 'all' ? undefined : status === 'active',
    };
    updateFilters(newFilters);
    fetchOrganizations(1, newFilters);
  }, [filters, updateFilters, fetchOrganizations]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    updateFilters(emptyFilters);
    fetchOrganizations(1, emptyFilters);
  }, [updateFilters, fetchOrganizations]);

  // Handle organization actions
  const handleCreate = useCallback(() => {
    navigate('/admin/organizations/create');
  }, [navigate]);

  const handleView = useCallback((organization: Organization) => {
    navigate(`/admin/organizations/${organization.id}`);
  }, [navigate]);

  const handleEdit = useCallback((organization: Organization) => {
    navigate(`/admin/organizations/${organization.id}/edit`);
  }, [navigate]);

  const handleDelete = useCallback(async (organization: Organization) => {
    // Prevent deletion of current user's organization
    if (user?.organization?.id === organization.id) {
      showError('Cannot Delete', 'You cannot delete your own organisation.');
      return;
    }

    if (!confirm(`Are you sure you want to delete organisation "${organization.name}"?`)) {
      return;
    }

    try {
      await organizationService.deleteOrganization(organization.id);
      showSuccess('Organisation Deleted', `Organisation "${organization.name}" has been deleted successfully.`);
      refresh();
    } catch (error) {
      console.error('Error deleting organization:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete organisation.';
      showError('Delete Failed', errorMessage);
    }
  }, [user?.organization?.id, showSuccess, showError, refresh]);


  // Define action definitions for the table
  const actionDefinitions: ActionDefinition<Organization>[] = [
    {
      id: 'view',
      label: 'View Organisation',
      icon: <EyeIcon size={16} />,
      onClick: handleView,
      intent: 'outline',
    },
    {
      id: 'edit',
      label: 'Edit Organisation',
      icon: <EditIcon size={16} />,
      onClick: handleEdit,
      intent: 'outline',
    },
    {
      id: 'delete',
      label: 'Delete Organisation',
      icon: <TrashIcon size={16} />,
      onClick: handleDelete,
      intent: 'danger',
      disabled: (organization) => organization.id === user?.organization?.id,
    },
  ];

  // ✅ NEW: 100% PATTERN-BASED COLUMNS - Complete standardization achieved!
  const columns = [
    // ✅ Name using textColumn pattern
    textColumn<Organization>('name', {
      header: 'Name'
    }),
    
    // ✅ Code using textColumn pattern
    textColumn<Organization>('code', {
      header: 'Code'
    }),
    
    // ✅ Type using textColumn pattern with transform
    textColumn<Organization>('type', {
      header: 'Type',
      transform: (value) => ORGANIZATION_TYPE_LABELS[value as OrganizationType] || value
    }),
    
    // ✅ User count using numberColumn pattern
    numberColumn<Organization>('userCount', {
      header: 'Users',
      emptyDisplay: '0'
    }),
    
    // ✅ City using textColumn pattern with fallback
    textColumn<Organization>('city', {
      header: 'City',
      emptyDisplay: '-'
    }),
    
    // ✅ Status using activeColumn helper with perfect defaults
    activeColumn<Organization>('isActive', 'Status'),
    
    // ✅ Created date using dateColumn pattern
    dateColumn<Organization>('createdAt', {
      header: 'Created',
      format: 'relative'
    }),
    
    // ✅ Actions using actionsColumn pattern
    actionsColumn<Organization>(actionDefinitions, {
      size: 'md'
    }),
  ];

  // Organization type options for select
  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...Object.entries(ORGANIZATION_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organisation Management</h1>
          <p className="text-gray-600 mt-1">
            Manage organisations, types, and settings
          </p>
        </div>
        <PermissionGate permission="organizations:create">
          <PositiveButton
            onClick={handleCreate}
          >
            Create Organisation
          </PositiveButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          {(filters.search || filters.type || filters.isActive !== undefined) && (
            <NeutralButton
              onClick={handleClearFilters}
              className="text-sm"
            >
              Clear Filters
            </NeutralButton>
          )}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <TextInput
            label="Search Organisations"
            placeholder="Search by name, code, or city..."
            value={filters.search || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
          />
          <Select
            label="Organisation Type"
            options={typeOptions}
            value={filters.type || 'all'}
            onValueChange={(value: string) => handleTypeFilter(value as OrganizationType | "all")}
            placeholder="All Types"
          />
          <Select
            label="Status"
            options={statusOptions}
            value={filters.isActive === undefined ? 'all' : filters.isActive ? 'active' : 'inactive'}
            onValueChange={(value: string) => handleStatusFilter(value as "all" | "active" | "inactive")}
            placeholder="All Status"
          />
        </div>
        {(filters.search || filters.type || filters.isActive !== undefined) && (
          <div className="text-sm text-gray-600">
            Active filters: {[
              filters.search && `Search: "${filters.search}"`,
              filters.type && `Type: ${ORGANIZATION_TYPE_LABELS[filters.type] || filters.type}`,
              filters.isActive !== undefined && `Status: ${filters.isActive ? 'Active' : 'Inactive'}`
            ].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Organisations</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <NeutralButton onClick={refresh}>
                  Try Again
                </NeutralButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organizations Table */}
      <DataTable
        data={organizations}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        loading={loading}
        manualPagination={true}
        pageCount={totalPages}
        rowCount={total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1; // Convert 0-based to 1-based
          const newPageSize = paginationState.pageSize;
          
          // Let the DataTable handle pagination state, just fetch data
          fetchOrganizations(newPage, filters, newPageSize);
        }}
        state={{
          pagination: {
            pageIndex: currentPage - 1, // Convert 1-based to 0-based
            pageSize: pageSize,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          enableFastNavigation: true,
          enableJumpToPage: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />
    </div>
  );
} 